import { OpenIMConfig, JFCChatConfig } from './config';
import { ApiResult, TokenData } from '@/types';

// Token cache for OpenIM user tokens
interface TokenCache {
    token: string;
    expiresAt: number;
}

// User role cache
interface RoleCache {
    isSalesman: boolean;
    customerNo?: string;
    cachedAt: number;
}

const userTokenCache: { [key: string]: TokenCache } = {};
const userRoleCache: { [key: string]: RoleCache } = {};

// In-flight request deduplication - prevents multiple simultaneous requests for the same user
const pendingTokenRequests: { [key: string]: Promise<string | null> } = {};
const pendingRoleRequests: { [key: string]: Promise<UserRoleResponse | null> } = {};

// Role cache duration (30 minutes - roles don't change frequently)
const ROLE_CACHE_DURATION = 30 * 60 * 1000;

// ============================================
// User Role Check (JFC Chat API)
// ============================================

export interface UserRoleResponse {
    isSalesman: boolean;
    customerNo?: string;
}

/**
 * Get user role using JFC token
 * @param jfcToken - JFC authentication token (Bearer token from e-commerce login)
 * @param email - User's email address
 * @returns { isSalesman: boolean } or null on error
 */
export async function getUserRole(jfcToken: string, email: string): Promise<UserRoleResponse | null> {
    const cacheKey = email;
    const now = Date.now();

    // Check cache first
    if (userRoleCache[cacheKey] && userRoleCache[cacheKey].cachedAt > now - ROLE_CACHE_DURATION) {
        return { isSalesman: userRoleCache[cacheKey].isSalesman, customerNo: userRoleCache[cacheKey].customerNo };
    }

    // If there's already a pending request for this user, wait for it
    if (cacheKey in pendingRoleRequests) {
        return pendingRoleRequests[cacheKey];
    }

    // Create the actual request and store it for deduplication
    const requestPromise = (async (): Promise<UserRoleResponse | null> => {
        try {
            const response = await fetch(`${JFCChatConfig.apiAddr}${JFCChatConfig.userRoleEndpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${jfcToken}`
                },
                body: JSON.stringify({ email })
            });

            if (!response.ok) {
                console.error('getUserRole failed with status:', response.status);
                return null;
            }

            const result = await response.json();

            // Handle both wrapped response format (errCode/errMsg/data) and direct response
            if (result.errCode !== undefined && result.errCode !== 0) {
                console.error('getUserRole API error:', result.errMsg);
                return null;
            }

            // Extract isSalesman and customerNo from various possible locations
            const isSalesman = result.isSalesman ?? result.data?.isSalesman ?? false;
            const customerNo = result.customerNo ?? result.data?.customerNo;

            // Cache the result
            userRoleCache[cacheKey] = {
                isSalesman,
                customerNo,
                cachedAt: Date.now()
            };

            return { isSalesman, customerNo };
        } catch (error) {
            console.error('Error getting user role:', error);
            return null;
        } finally {
            // Clean up pending request
            delete pendingRoleRequests[cacheKey];
        }
    })();

    pendingRoleRequests[cacheKey] = requestPromise;
    return requestPromise;
}

// ============================================
// User Token Management (JFC Chat API)
// ============================================

/**
 * Get OpenIM user token using JFC token
 * @param jfcToken - JFC authentication token (Bearer token from e-commerce login)
 * @param email - User's email address
 * @param platformID - OpenIM platform ID for this runtime
 * @returns OpenIM token string or null on error
 */
export async function getUserToken(
    jfcToken: string,
    email: string,
    platformID: number = OpenIMConfig.platformId
): Promise<string | null> {
    const now = Date.now();
    const cacheKey = `${email}:${platformID}`;

    // Check cache (with 5-minute buffer)
    if (userTokenCache[cacheKey] && userTokenCache[cacheKey].expiresAt > now + 5 * 60 * 1000) {
        return userTokenCache[cacheKey].token;
    }

    // If there's already a pending request for this user, wait for it
    if (cacheKey in pendingTokenRequests) {
        return pendingTokenRequests[cacheKey];
    }

    // Create the actual request and store it for deduplication
    const requestPromise = (async (): Promise<string | null> => {
        try {
            const response = await fetch(`${JFCChatConfig.apiAddr}${JFCChatConfig.usersTokenEndpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${jfcToken}`
                },
                body: JSON.stringify({
                    email: email,
                    platformID
                })
            });

            if (!response.ok) {
                console.error('getUserToken failed with status:', response.status);
                return null;
            }

            const result = await response.json();

            // Handle both wrapped response format (errCode/errMsg/data) and direct response
            let token: string | undefined;
            let expireTimeSeconds: number | undefined;

            if (result.errCode !== undefined) {
                // Standard API response format with errCode wrapper
                if (result.errCode !== 0) {
                    console.error('Failed to get user token:', result.errMsg);
                    return null;
                }
                token = result.data?.token;
                expireTimeSeconds = result.data?.expireTimeSeconds;
            } else {
                // Direct response format without errCode wrapper
                // Try to extract token from common field names
                token = result.token || result.openIMToken || result.imToken || result.access_token;
                expireTimeSeconds = result.expireTimeSeconds || result.expires_in || result.expireTime;
            }

            if (!token) {
                console.error('Failed to get user token: no token found in response', result);
                return null;
            }

            // Cache the token (default to 1 hour if no expiration provided)
            const cacheDuration = (expireTimeSeconds || 3600) * 1000;
            userTokenCache[cacheKey] = {
                token,
                expiresAt: Date.now() + cacheDuration
            };

            return token;
        } catch (error) {
            console.error('Error fetching user token:', error);
            return null;
        } finally {
            // Clean up pending request
            delete pendingTokenRequests[cacheKey];
        }
    })();

    pendingTokenRequests[cacheKey] = requestPromise;
    return requestPromise;
}

// ============================================
// Token Cache Management
// ============================================

/**
 * Clear cached token for a specific user
 * @param email - User's email address
 */
export function clearUserTokenCache(email: string): void {
    delete userTokenCache[email];
}

/**
 * Clear all cached tokens
 */
export function clearAllTokenCache(): void {
    Object.keys(userTokenCache).forEach(key => delete userTokenCache[key]);
}

// ============================================
// Group Management (JFC Chat API)
// ============================================

/**
 * Check if a group exists for the given customerID
 * @param jfcToken - JFC authentication token
 * @param customerID - The customer ID (which is also the groupID)
 * @returns { exists: boolean }
 */
export async function checkGroupExistence(jfcToken: string, customerID: string): Promise<{ exists: boolean }> {
    try {
        const response = await fetch(`${JFCChatConfig.apiAddr}${JFCChatConfig.clientRoomsEndpoint}/${customerID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jfcToken}`
            }
        });

        if (response.ok) {
            return { exists: true };
        } else if (response.status === 404) {
            return { exists: false };
        }

        console.warn('checkGroupExistence failed with status:', response.status);
        return { exists: false };
    } catch (error) {
        console.error('Error checking group existence:', error);
        return { exists: false };
    }
}

// ============================================
// Message Management (JFC Chat API)
// ============================================

export interface MarkMessageAsReadRequest {
    clientMsgID: string;
    customerID: string;
    userEmail: string;
}

/**
 * Notify JFC backend that a customer has read up to a specific message.
 *
 * In the client read-receipt flow, OpenIM receives a custom `read_status`
 * message with all read message IDs, while this API is called with only
 * the last message ID from that batch as a high-water mark.
 *
 * @param jfcToken - JFC authentication token
 * @param request - Read marker payload
 *   - clientMsgID: last read message ID in the current batch
 *   - customerID: room/group identifier
 *   - userEmail: current reader email
 * @returns Success boolean
 */
/**
 * Close a room (update room status to closed)
 * @param jfcToken - JFC authentication token
 * @param customerID - The unique customer ID (customerId), used as the OpenIM groupID
 * @returns Room close response or null on error
 */
export async function closeRoom(jfcToken: string, customerID: string): Promise<{ closedAt: string; customerId: string; isOpen: boolean; openedAt: string } | null> {
    try {
        const response = await fetch(`${JFCChatConfig.apiAddr}${JFCChatConfig.clientRoomsEndpoint}/${customerID}/close`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jfcToken}`
            }
        });

        if (!response.ok) {
            console.error('closeRoom failed with status:', response.status);
            return null;
        }

        const result = await response.json();

        if (result.success === false) {
            console.error('closeRoom API error:', result.message || result.error);
            return null;
        }

        return result.data || null;
    } catch (error) {
        console.error('Error closing room:', error);
        return null;
    }
}

/**
 * Open a room (update room status to open)
 * @param jfcToken - JFC authentication token
 * @param customerID - The unique customer ID (customerId), used as the OpenIM groupID
 * @returns Room open response or null on error
 */
export async function openRoom(jfcToken: string, customerID: string): Promise<{ closedAt: string; customerId: string; isOpen: boolean; openedAt: string } | null> {
    try {
        const response = await fetch(`${JFCChatConfig.apiAddr}${JFCChatConfig.clientRoomsEndpoint}/${customerID}/open`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jfcToken}`
            }
        });

        if (!response.ok) {
            console.error('openRoom failed with status:', response.status);
            return null;
        }

        const result = await response.json();

        if (result.success === false) {
            console.error('openRoom API error:', result.message || result.error);
            return null;
        }

        return result.data || null;
    } catch (error) {
        console.error('Error opening room:', error);
        return null;
    }
}

export async function markMessageAsRead(jfcToken: string, request: MarkMessageAsReadRequest): Promise<boolean> {
    try {
        const response = await fetch(`${JFCChatConfig.apiAddr}${JFCChatConfig.messageReadEndpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jfcToken}`
            },
            body: JSON.stringify(request)
        });

        if (!response.ok) {
            console.error('markMessageAsRead failed with status:', response.status);
            return false;
        }

        const result = await response.json();

        // Handle both wrapped response format (errCode/errMsg/data) and direct response
        if (result.errCode !== undefined && result.errCode !== 0) {
            console.error('markMessageAsRead API error:', result.errMsg);
            return false;
        }

        return true;
    } catch (error) {
        console.error('Error in markMessageAsRead:', error);
        return false;
    }
}
